import { Fragment, useMemo, useState } from "react";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { useResource, formatDateTime, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { locationCreateFields } from "../lib/formOptions";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { Employee, Incident, LocationRow, Participant } from "../lib/types";

const SEVERITY = ["low", "medium", "high", "critical"].map((s) => ({ value: s, label: titleCase(s) }));
const STATUS = ["open", "under_review", "resolved", "closed"].map((s) => ({ value: s, label: titleCase(s) }));

const severityChip = (s: Incident["severity"]) =>
    s === "critical" || s === "high" ? "block" : s === "medium" ? "warn" : "mute";

const statusChip = (s: Incident["status"]) =>
    s === "open" ? "block" : s === "under_review" ? "warn" : s === "resolved" ? "info" : "pass";

/** Fields the API exposes but the summary type does not carry. */
interface IncidentFull extends Incident {
    participant_id: string | null;
    location_id: string | null;
    employee_id: string | null;
    notified_at: string | null;
    notify_due_at: string | null;
    investigation_findings: string | null;
    follow_up: string | null;
    closure_note: string | null;
    is_draft: boolean;
    submitted_at: string | null;
}

/**
 * Whether a staff member's amendment window is still open, mirrored from the
 * database function that actually enforces it. Two hours from submission; a
 * draft is always editable. The server remains the authority -- this only
 * decides which buttons to draw.
 */
const editWindowOpen = (incident: IncidentFull): boolean => {
    if (incident.is_draft) return true;
    if (!incident.submitted_at) return false;
    return Date.now() <= new Date(incident.submitted_at).getTime() + 2 * 60 * 60 * 1000;
};

const minutesLeft = (incident: IncidentFull): number =>
    incident.submitted_at
        ? Math.max(0, Math.round((new Date(incident.submitted_at).getTime() + 7_200_000 - Date.now()) / 60_000))
        : 0;

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; incident: IncidentFull }
    | { kind: "review"; incident: IncidentFull }
    | { kind: "investigate"; incident: IncidentFull }
    | { kind: "close"; incident: IncidentFull }
    | { kind: "notify"; incident: IncidentFull }
    | null;

export const Incidents = () => {
    const { user } = useAuth();
    const canReview = user?.role === "admin" || user?.role === "manager" || user?.role === "supervisor";
    const canReport = Boolean(user);
    const isStaff = user?.role === "staff";

    const incidents = useResource<IncidentFull[]>("/incidents");
    const participants = useResource<Participant[]>("/participants");
    const locations = useResource<LocationRow[]>("/locations");
    const employees = useResource<Employee[]>("/employees");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [expanded, setExpanded] = useState<string | null>(null);
    const [dialog, setDialog] = useState<Dialog>(null);

    const refresh = () => { incidents.reload(); setDialog(null); };

    const nameOf = useMemo(() => {
        const p = new Map((participants.data ?? []).map((x) => [x.id, x.full_name]));
        const l = new Map((locations.data ?? []).map((x) => [x.id, x.name]));
        const e = new Map((employees.data ?? []).map((x) => [x.id, x.full_name]));
        return {
            participant: (id: string | null) => (id ? p.get(id) ?? "Unknown" : "—"),
            location: (id: string | null) => (id ? l.get(id) ?? "Unknown" : "—"),
            employee: (id: string | null) => (id ? e.get(id) ?? "Unknown" : "—"),
        };
    }, [participants.data, locations.data, employees.data]);

    const fields: Field[] = [
        { name: "title", label: "What happened", kind: "text", required: true },
        { name: "description", label: "Description", kind: "textarea", required: true },
        { name: "occurred_at", label: "When it occurred", kind: "datetime", required: true, half: true },
        { name: "severity", label: "Severity", kind: "select", options: SEVERITY, half: true },
        { name: "participant_id", label: "Participant involved", kind: "select", half: true,
          options: (participants.data ?? []).map((p) => ({ value: p.id, label: p.full_name })) },
        { name: "location_id", label: "Location", kind: "select", half: true,
          options: (locations.data ?? []).map((l) => ({ value: l.id, label: l.name })) ,
          addNew: { title: "New location", path: "/locations", fields: locationCreateFields() } },
        { name: "employee_id", label: "Worker involved", kind: "select", half: true,
          options: (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name })) },
        { name: "is_ndis_reportable", label: "NDIS reportable incident", kind: "checkbox",
          help: "A reportable incident cannot be closed until the Commission notification is recorded." },
        { name: "action_taken", label: "Immediate action taken", kind: "textarea" },
        ...(isStaff ? [{
            name: "is_draft", label: "Save as draft (submit later)", kind: "checkbox" as const,
            help: "A draft stays editable. Once submitted, you can amend it for 2 hours; after that it locks permanently.",
        }] : []),
    ];

    const filtered = (incidents.data ?? []).filter((i) =>
        matches(search, i.title, i.description) &&
        (!filters.status || i.status === filters.status) &&
        (!filters.severity || i.severity === filters.severity) &&
        (!filters.reportable || String(i.is_ndis_reportable) === filters.reportable)
    );

    if (incidents.error) return <div className="error-banner">{incidents.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <EntityForm title="Report an Incident" path="/incidents" fields={fields}
                    fixed={{ reported_by: user?.id }}
                    subtitle="Anyone may raise an incident. Reporting is never blocked — an unreported incident is a worse failure than an over-reported register."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title="Edit incident" path="/incidents" fields={fields} initial={dialog.incident}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "notify" && (
                <EntityForm title="Record NDIS Commission notification" path="/incidents"
                    initial={dialog.incident}
                    fields={[
                        { name: "notified_at", label: "Notified at", kind: "datetime", required: true,
                          help: "Recording this is what unlocks closure for a reportable incident." },
                    ]}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "review" && (
                <ConfirmDialog
                    title="Move this incident to review?"
                    message="It is marked as under review and your name is recorded as the reviewer."
                    confirmLabel="Start review"
                    request={{ method: "post", path: `/incidents/${dialog.incident.id}/review` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "investigate" && (
                <InvestigateDialog incident={dialog.incident} onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "close" && (
                <ConfirmDialog
                    title="Close this incident?"
                    message="It is marked closed and drops out of the open register."
                    warning={dialog.incident.is_ndis_reportable && !dialog.incident.notified_at
                        ? "This is an NDIS-reportable incident and no Commission notification has been recorded. Closure will be refused until you record it."
                        : "The record and its investigation stay in the register permanently."}
                    confirmLabel="Close incident" requireReason reasonLabel="Closure note"
                    request={{ method: "post", path: `/incidents/${dialog.incident.id}/close` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search incidents…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "status", allLabel: "All Status", options: STATUS },
                    { name: "severity", allLabel: "All Severity", options: SEVERITY },
                    { name: "reportable", allLabel: "All Incidents", options: [
                        { value: "true", label: "NDIS reportable" },
                        { value: "false", label: "Not reportable" },
                    ] },
                ]}
                resultNote={
                    filtered.length !== (incidents.data?.length ?? 0)
                        ? `Showing ${filtered.length} of ${incidents.data?.length ?? 0} incidents`
                        : undefined
                }
                action={canReport && (
                    <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Report Incident</button>
                )}
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Occurred</th><th>Incident</th><th>Participant</th>
                                    <th>Severity</th><th>Status</th><th>Reportable</th>
                                    {(canReview || isStaff) && <th style={{ textAlign: "right" }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((incident) => (
                                    <Fragment key={incident.id}>
                                        <tr className="clickable"
                                            onClick={() => setExpanded(expanded === incident.id ? null : incident.id)}>
                                            <td className="mono">{formatDateTime(incident.occurred_at)}</td>
                                            <td style={{ fontWeight: 600 }}>{incident.title}</td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                                                    <InitialsAvatar name={nameOf.participant(incident.participant_id)} size={24} />
                                                    {nameOf.participant(incident.participant_id)}
                                                </div>
                                            </td>
                                            <td><span className={`chip ${severityChip(incident.severity)}`}>{incident.severity}</span></td>
                                            <td>
                                                {incident.is_draft
                                                    ? <span className="chip mute">Draft</span>
                                                    : <span className={`chip ${statusChip(incident.status)}`}>{titleCase(incident.status)}</span>}
                                            </td>
                                            <td>
                                                {incident.is_ndis_reportable ? (
                                                    <span className={`chip ${incident.notified_at ? "pass" : "block"}`}>
                                                        {incident.notified_at ? "notified" : "not notified"}
                                                    </span>
                                                ) : <span className="chip mute">no</span>}
                                            </td>
                                            {isStaff && (
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="row-actions">
                                                        {editWindowOpen(incident) ? (
                                                            <>
                                                                <button className="icon-btn" onClick={() => setDialog({ kind: "edit", incident })}>
                                                                    {incident.is_draft ? "Edit draft" : `Amend (${minutesLeft(incident)} min left)`}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <span className="chip mute" title="The 2-hour amendment window has closed. The record is now read-only.">
                                                                locked
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            {canReview && (
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="row-actions">
                                                        <button className="icon-btn" onClick={() => setDialog({ kind: "edit", incident })}>Edit</button>
                                                        {incident.status === "open" && (
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "review", incident })}>Review</button>
                                                        )}
                                                        {incident.status !== "closed" && (
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "investigate", incident })}>Investigate</button>
                                                        )}
                                                        {incident.is_ndis_reportable && !incident.notified_at && (
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "notify", incident })}>Record notification</button>
                                                        )}
                                                        {incident.status !== "closed" && (
                                                            <button className="icon-btn danger" onClick={() => setDialog({ kind: "close", incident })}>Close</button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                        {expanded === incident.id && (
                                            <tr>
                                                <td colSpan={canReview || isStaff ? 7 : 6} style={{ background: "var(--surface-sunk)" }}>
                                                    <dl className="kv">
                                                        <dt>Description</dt><dd>{incident.description}</dd>
                                                        <dt>Location</dt><dd>{nameOf.location(incident.location_id)}</dd>
                                                        <dt>Worker involved</dt><dd>{nameOf.employee(incident.employee_id)}</dd>
                                                        {incident.action_taken && (<><dt>Action taken</dt><dd>{incident.action_taken}</dd></>)}
                                                        {incident.investigation_findings && (
                                                            <><dt>Investigation findings</dt><dd>{incident.investigation_findings}</dd></>
                                                        )}
                                                        {incident.follow_up && (<><dt>Follow-up</dt><dd>{incident.follow_up}</dd></>)}
                                                        {incident.notified_at && (
                                                            <><dt>Commission notified</dt><dd>{formatDateTime(incident.notified_at)}</dd></>
                                                        )}
                                                        {incident.closure_note && (<><dt>Closure note</dt><dd>{incident.closure_note}</dd></>)}
                                                    </dl>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={canReview || isStaff ? 7 : 6} className="empty">No incidents match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Investigation is its own form rather than a ConfirmDialog because it captures
 * three separate pieces of evidence, and the API requires the findings to be
 * substantive before it will accept them.
 */
const InvestigateDialog = ({
    incident, onClose, onDone,
}: { incident: IncidentFull; onClose: () => void; onDone: () => void }) => (
    <EntityForm
        title="Record investigation"
        subtitle={incident.title}
        path={`/incidents/${incident.id}/investigate`}
        fields={[
            { name: "findings", label: "Findings", kind: "textarea", required: true,
              help: "What was established. At least 10 characters." },
            { name: "actionTaken", label: "Action taken", kind: "textarea" },
            { name: "followUp", label: "Follow-up required", kind: "textarea" },
        ]}
        initial={{
            findings: incident.investigation_findings ?? "",
            actionTaken: incident.action_taken ?? "",
            followUp: incident.follow_up ?? "",
        }}
        onClose={onClose}
        onSaved={onDone}
    />
);
